import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { videoService } from '../../services'
import { useAuthStore } from '../../stores'
import type { Video, VideoVisibility } from '../../types/database.types'
import { Button, Input, LoadingScreen, Spinner } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { VideoThumbnail, ThumbnailSelector } from '../../components/video'

const CATEGORIES = [
  'Strength',
  'Mobility',
  'Cardio',
  'Stretching',
  'Balance',
  'Rehabilitation',
  'Warm-up',
  'Cool-down',
  'Core',
  'Upper Body',
  'Lower Body',
  'Full Body',
]

export function VideoEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [video, setVideo] = useState<Video | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Form state
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')
  const [visibility, setVisibility] = useState<VideoVisibility>('public')
  const [thumbnailTime, setThumbnailTime] = useState<number | null>(null)

  useEffect(() => {
    const loadVideo = async () => {
      if (!id || !profile) return

      try {
        const videoData = await videoService.getVideoById(id)

        if (!videoData) {
          toast.error('Video not found')
          navigate('/dashboard/videos')
          return
        }

        // Check ownership
        if (videoData.user_id !== profile.id) {
          toast.error('You do not have permission to edit this video')
          navigate('/dashboard/videos')
          return
        }

        setVideo(videoData)
        setTitle(videoData.title)
        setDescription(videoData.description || '')
        setCategory(videoData.category || '')
        setTags(videoData.tags?.join(', ') || '')
        setVisibility(videoData.visibility || 'public')
        setThumbnailTime(videoData.thumbnail_time ?? null)
      } catch (error) {
        console.error('Failed to load video:', error)
        toast.error('Failed to load video')
        navigate('/dashboard/videos')
      } finally {
        setIsLoading(false)
      }
    }

    loadVideo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!video) return

    if (!title.trim()) {
      toast.error('Please enter a video title')
      return
    }

    setIsSaving(true)

    try {
      await videoService.updateVideo(video.id, {
        title: title.trim(),
        description: description.trim() || null,
        category: category || null,
        tags: tags
          ? tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : null,
        visibility,
        thumbnail_time: thumbnailTime,
      })

      toast.success('Video updated successfully')
      navigate('/dashboard/videos')
    } catch (error) {
      console.error('Failed to update video:', error)
      toast.error('Failed to update video')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading video..." />
  }

  if (!video) {
    return null
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Edit Video</h1>
        <p className="mt-1 text-gray-600">
          Update your video's details and visibility settings.
        </p>
      </div>

      {/* Video preview */}
      <div className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start gap-6">
          <div className="w-48 flex-shrink-0">
            <VideoThumbnail video={video} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              {video.status === 'ready' ? (
                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  Ready
                </span>
              ) : video.status === 'processing' ? (
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
                  Processing
                </span>
              ) : video.status === 'error' ? (
                <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                  Error
                </span>
              ) : (
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  {video.status || 'Unknown'}
                </span>
              )}
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Duration</dt>
                <dd className="text-gray-900">
                  {video.duration_seconds
                    ? `${Math.floor(video.duration_seconds / 60)}:${String(
                        Math.floor(video.duration_seconds % 60)
                      ).padStart(2, '0')}`
                    : '--:--'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Resolution</dt>
                <dd className="text-gray-900">{video.resolution || 'N/A'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Views</dt>
                <dd className="text-gray-900">{video.view_count || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Likes</dt>
                <dd className="text-gray-900">{video.like_count || 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Uploaded</dt>
                <dd className="text-gray-900">
                  {new Date(video.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Thumbnail Selector - only show when video is ready */}
      {video.status === 'ready' && video.mux_playback_id && video.duration_seconds && (
        <div className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
          <ThumbnailSelector
            playbackId={video.mux_playback_id}
            duration={video.duration_seconds}
            currentTime={thumbnailTime}
            onChange={setThumbnailTime}
            disabled={isSaving}
          />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Single Leg Squat for Knee Rehab"
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the exercise, target muscles, and any important form cues..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a category</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <Input
          label="Tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="knee, rehab, strength (comma separated)"
          helperText="Add tags to help others find your video"
        />

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Visibility
          </label>
          <div className="space-y-2">
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="public"
                checked={visibility === 'public'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Public</p>
                <p className="text-sm text-gray-500">
                  Anyone can find and watch this video
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="organization"
                checked={visibility === 'organization'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Organization Only</p>
                <p className="text-sm text-gray-500">
                  Only members of your organization can view
                </p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="visibility"
                value="private"
                checked={visibility === 'private'}
                onChange={(e) =>
                  setVisibility(e.target.value as VideoVisibility)
                }
                className="mt-0.5"
              />
              <div>
                <p className="font-medium text-gray-900">Private</p>
                <p className="text-sm text-gray-500">
                  Only you can view this video
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard/videos')}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}
